/**
 * Role edit form.
 * Loads the role + bindings, hydrates general / appearance / permissions, then PATCHes in one submit.
 */
import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { CollapseReplaceForm } from '@/components/common/CollapseReplaceForm';
import { RoleGeneralSection } from './role-form-builder/RoleGeneralSection';
import { RoleAppearanceSection } from './role-form-builder/RoleAppearanceSection';
import { RolePermissionsSection } from './role-form-builder/RolePermissionsSection';
import {
  applyRoleFormField,
  buildRoleGroupOptions,
  buildUpdateRolePayload,
  convertRulesToLegacyBindings,
  emptyRoleForm,
  hydrateRoleFormFromGet,
  isEditRoleValid,
} from './role-form-utils';
import type { RoleFormState } from '@/types/role.types';
import {
  useRole,
  useUpdateRole,
  useRoleGroups,
  useRoleInterfaceBindings,
  useReplaceRoleBindings,
} from '@/hooks/useRoles';
import { useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { updateConfigAccess } from '@/services/post-its.service';
import type { InterfaceElement } from '@/types/interface-elements.types';

/** Props for {@link EditRoleView}. */
interface EditRoleViewProps {
  roleId: string;
  onClose: () => void;
  onSuccess: () => void;
}

/** Role edit form. */
export const EditRoleView: React.FC<EditRoleViewProps> = ({ roleId, onClose, onSuccess }) => {
  const { data: role, isLoading, isError, error } = useRole(roleId);
  useUnitGetNotFound({
    isError,
    error,
    catalogKey: 'roleNotFound',
    onClose,
    entityId: roleId,
  });
  const { data: existingBindings } = useRoleInterfaceBindings(roleId);
  const { mutateAsync, isPending } = useUpdateRole();
  const { mutateAsync: replaceBindings, isPending: replacingBindings } = useReplaceRoleBindings();
  const { data: roleGroups = [] } = useRoleGroups();

  const [form, setForm] = React.useState<RoleFormState>(emptyRoleForm);
  const [isHydrated, setIsHydrated] = React.useState(false);
  const [interfaceElements, setInterfaceElements] = React.useState<
    Record<string, InterfaceElement[]>
  >({});
  const [postitConfigVisible, setPostitConfigVisible] = React.useState(false);

  const updatePostitAccessMutation = useMutation({
    mutationFn: ({ roleId, visible }: { roleId: string; visible: boolean }) =>
      updateConfigAccess(roleId, visible),
    onSuccess: () => {
      toast.success('Accès aux post-its mis à jour');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  React.useEffect(() => {
    if (role && existingBindings !== undefined && Object.keys(interfaceElements).length > 0) {
      const hydrated = hydrateRoleFormFromGet(role, existingBindings, interfaceElements);
      setPostitConfigVisible(hydrated.postitConfigVisible);
      setForm(hydrated.form);
      setIsHydrated(true);
    }
  }, [role, existingBindings, interfaceElements]);

  const handleFieldChange = (field: keyof RoleFormState, value: unknown) => {
    setForm((prev) => applyRoleFormField(prev, field, value));
  };

  const groupOptions = React.useMemo(() => buildRoleGroupOptions(roleGroups), [roleGroups]);
  const isValid = isEditRoleValid(form);
  const isBusy = isPending || replacingBindings;

  /** Validate, PATCH the role, then close. */
  const handleSubmit = async () => {
    if (!isValid) return;
    try {
      await mutateAsync({ id: roleId, payload: buildUpdateRolePayload(form) });

      if (Object.keys(interfaceElements).length > 0) {
        const legacyBindings = convertRulesToLegacyBindings(form.bindings, interfaceElements);
        await replaceBindings({ roleId, bindings: legacyBindings });
      }

      toast.success('Rôle et accès mis à jour avec succès !');
      onSuccess?.();
      onClose();
    } catch (err: unknown) {
      toast.error(getErrorMessage(err));
    }
  };

  if (!isLoading && role?.isSystem) {
    return (
      <CollapseReplaceForm
        formType="edit"
        title={role.label}
        subtitle="Ce rôle système ne peut pas être modifié."
        onClose={onClose}
        onSubmit={onClose}
        submitLabel="Fermer"
        isSubmitting={false}
        isValid={true}
      >
        <p className="text-sm text-muted-foreground">
          Les rôles système sont gérés automatiquement et ne peuvent pas être modifiés manuellement.
        </p>
      </CollapseReplaceForm>
    );
  }

  return (
    <CollapseReplaceForm
      formType="edit"
      title="Modifier le rôle"
      subtitle="Modifiez les informations du rôle"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Enregistrer"
      isSubmitting={isPending}
      isValid={isValid && isHydrated}
      isLoading={isLoading || !role}
    >
      <RoleGeneralSection form={form} onChange={handleFieldChange} groupOptions={groupOptions} />
      <RoleAppearanceSection form={form} onChange={handleFieldChange} />
      <RolePermissionsSection
        form={form}
        onBindingsChange={(bindings) => handleFieldChange('bindings', bindings)}
        onElementsLoaded={setInterfaceElements}
        disabled={isBusy || updatePostitAccessMutation.isPending}
        postItsAccess={
          role
            ? {
                fallbackRoleGroup: role.roleGroup,
                fallbackHierarchyLevel: role.hierarchyLevel ?? null,
                visible: postitConfigVisible,
                onVisibleChange: (visible) => {
                  setPostitConfigVisible(visible);
                  updatePostitAccessMutation.mutate({ roleId: role.id, visible });
                },
              }
            : undefined
        }
      />
    </CollapseReplaceForm>
  );
};
